import { Box, CssBaseline } from "@mui/material";
import { useEffect } from "react";
import { Outlet, useLocation } from "react-router";
import { ThemeProvider } from "../../../styles/theme/theme-provider";
import { SettingsConsumer } from "../../../utils/contexts/settings-context";
import { AppToaster } from "./app-toaster";
import { Sidebar } from "./sidebar/sidebar";
import { SidebarUiProvider, useSidebarUi } from "./sidebar/sidebar-ui-context";

import type { DisplayTheme } from "../../../utils/contexts/settings-context";
import type { BoxProps } from "@mui/material";
import type { FC } from "react";

interface ApplicationLayoutProps {
  themeOverride?: DisplayTheme;
  layout?: "content" | "default";
  slots?: {
    root?: {
      props?: BoxProps;
    };
    header?: {
      props?: BoxProps;
    };
  };
}

export const LightApplicationLayout: FC = () => <ApplicationLayout themeOverride="light" />;

export const ContentApplicationLayout: FC<ApplicationLayoutProps> = (props) => (
  <SidebarUiProvider>
    <ApplicationLayoutInner {...props} layout="content" />
  </SidebarUiProvider>
);

export const ApplicationLayout: FC<ApplicationLayoutProps> = (props) => (
  <SidebarUiProvider>
    <ApplicationLayoutInner {...props} />
  </SidebarUiProvider>
);

const ApplicationLayoutInner: FC<ApplicationLayoutProps> = ({
  themeOverride,
  slots,
  layout = "default",
}) => {
  const { setLayout, closeSidebar } = useSidebarUi();
  useEffect(() => {
    document.body.style.overflow = "hidden";
    setLayout(layout);
    return () => {
      document.body.style.overflow = "auto";
      setLayout("default");
    };
  }, [layout, setLayout]);

  const { pathname } = useLocation();

  useEffect(() => {
    closeSidebar();
  }, [closeSidebar, pathname]);

  return (
    <SettingsConsumer>
      {({ settings }) => (
        <ThemeProvider mode={themeOverride ?? settings.theme}>
          <CssBaseline />
          <AppToaster />
          <Sidebar variant="temporary" />
          <AppRoot {...slots?.root?.props}>
            <Outlet />
          </AppRoot>
        </ThemeProvider>
      )}
    </SettingsConsumer>
  );
};

const AppRoot: FC<BoxProps> = ({ children, sx = [], ...props }) => (
  <Box
    className="App-root"
    sx={[
      {
        "--sb-thumb-width": "80px",
        bgcolor: "background.paper",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
      },
      ...(sx instanceof Array ? sx : [sx]),
    ]}
    {...props}
  >
    {children}
  </Box>
);
